import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '~/lib/api'

// Club money is not team-bound: keys carry the season. Every key starts with 'finance', which the
// server invalidates after each change (and the month close after paying settlements).
export const financeKeys = {
  all: ['finance'] as const,
  overview: (season: string) => ['finance', season, 'overview'] as const,
  plans: ['finance', 'plans'] as const,
  charges: (season: string, filter: ChargeFilter) => ['finance', season, 'charges', filter] as const,
  accounts: ['finance', 'accounts'] as const,
  ledger: (season: string, filter: LedgerFilter) => ['finance', season, 'ledger', filter] as const,
  budgets: (season: string) => ['finance', season, 'budgets'] as const,
  sponsors: (season: string) => ['finance', season, 'sponsors'] as const,
}

type Data<F extends (...a: never[]) => unknown> = Awaited<ReturnType<F>>

const fetchOverview = (season: string) => unwrap(api.finance.overview.$get({ query: { season } }))
export const overviewQuery = (season: string) => queryOptions({ queryKey: financeKeys.overview(season), queryFn: () => fetchOverview(season) })
export type Overview = Data<typeof fetchOverview>
export type LedgerItem = Overview['recent'][number]

const fetchPlans = () => unwrap(api.finance.plans.$get())
export const plansQuery = () => queryOptions({ queryKey: financeKeys.plans, queryFn: fetchPlans })
export type FeePlan = Data<typeof fetchPlans>[number]

export type ChargeFilter = { status?: 'open' | 'invoiced' | 'paid' | 'overdue' | 'waived' | 'deferred' | 'outstanding'; planId?: string; q?: string }
const fetchCharges = (season: string, f: ChargeFilter, cursor?: string) =>
  unwrap(api.finance.charges.$get({ query: { season, limit: '50', ...f, ...(cursor ? { cursor } : {}) } as never }))
export const chargesQuery = (season: string, f: ChargeFilter) =>
  infiniteQueryOptions({
    queryKey: financeKeys.charges(season, f),
    queryFn: ({ pageParam }) => fetchCharges(season, f, pageParam || undefined),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })
export type ChargesPage = Data<typeof fetchCharges>
export type Charge = ChargesPage['items'][number]

const fetchAccounts = () => unwrap(api.finance.accounts.$get())
export const accountsQuery = () => queryOptions({ queryKey: financeKeys.accounts, queryFn: fetchAccounts })
export type Account = Data<typeof fetchAccounts>[number]

export type LedgerFilter = { direction?: 'in' | 'out'; category?: string; q?: string; budgetId?: string }
const fetchLedger = (season: string, f: LedgerFilter, cursor?: string) =>
  unwrap(api.finance.ledger.$get({ query: { season, limit: '50', ...f, ...(cursor ? { cursor } : {}) } as never }))
export const ledgerQuery = (season: string, f: LedgerFilter) =>
  infiniteQueryOptions({
    queryKey: financeKeys.ledger(season, f),
    queryFn: ({ pageParam }) => fetchLedger(season, f, pageParam || undefined),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })
export type LedgerPage = Data<typeof fetchLedger>
export type LedgerEntry = LedgerPage['items'][number]

const fetchBudgets = (season: string) => unwrap(api.finance.budgets.$get({ query: { season } }))
export const budgetsQuery = (season: string) => queryOptions({ queryKey: financeKeys.budgets(season), queryFn: () => fetchBudgets(season) })
export type BudgetsData = Data<typeof fetchBudgets>
export type Budget = BudgetsData['items'][number]

const fetchSponsors = (season: string) => unwrap(api.finance.sponsors.$get({ query: { season } }))
export const sponsorsQuery = (season: string) => queryOptions({ queryKey: financeKeys.sponsors(season), queryFn: () => fetchSponsors(season) })
export type SponsorsData = Data<typeof fetchSponsors>
export type Sponsor = SponsorsData['items'][number]
export type Sponsorship = Sponsor['sponsorships'][number]

function useRefresh() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ['finance'] })
}

// --- fees -----------------------------------------------------------------------------------

type PlanCreate = Parameters<typeof api.finance.plans.$post>[0]['json']
export function useSavePlan() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { id?: string; json: PlanCreate }) =>
      v.id ? unwrap(api.finance.plans[':id'].$patch({ param: { id: v.id }, json: v.json })) : unwrap(api.finance.plans.$post({ json: v.json })),
    onSuccess: refresh,
  })
}
export function useDeletePlan() {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (id: string) => unwrap(api.finance.plans[':id'].$delete({ param: { id } })), onSuccess: refresh })
}

type Generate = Parameters<typeof api.finance.charges.generate.$post>[0]['json']
export function useGenerateCharges() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (json: Generate) => unwrap(api.finance.charges.generate.$post({ json })),
    onSuccess: (data) => (data.preview ? undefined : refresh()),
    // Previews fail quietly while the form is incomplete; the final run shows its error.
    onError: () => {},
  })
}

type ChargePatch = Parameters<(typeof api.finance.charges)[':id']['$patch']>[0]['json']
export function useUpdateCharge() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { id: string; json: ChargePatch }) => unwrap(api.finance.charges[':id'].$patch({ param: { id: v.id }, json: v.json })),
    onSuccess: refresh,
  })
}

// --- ledger ---------------------------------------------------------------------------------

type LedgerCreate = Parameters<typeof api.finance.ledger.$post>[0]['json']
type LedgerPatch = Parameters<(typeof api.finance.ledger)[':id']['$patch']>[0]['json']
export function useCreateEntry() {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (json: LedgerCreate) => unwrap(api.finance.ledger.$post({ json })), onSuccess: refresh })
}
export function useUpdateEntry(id: string) {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (json: LedgerPatch) => unwrap(api.finance.ledger[':id'].$patch({ param: { id }, json })), onSuccess: refresh })
}
export function useReverseEntry() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { id: string; reason?: string }) => unwrap(api.finance.ledger[':id'].reverse.$post({ param: { id: v.id }, json: v.reason ? { reason: v.reason } : {} })),
    onSuccess: refresh,
  })
}

// --- budgets and sponsors -------------------------------------------------------------------

type BudgetCreate = Parameters<typeof api.finance.budgets.$post>[0]['json']
export function useSaveBudget() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { id?: string; json: BudgetCreate }) =>
      v.id ? unwrap(api.finance.budgets[':id'].$patch({ param: { id: v.id }, json: v.json })) : unwrap(api.finance.budgets.$post({ json: v.json })),
    onSuccess: refresh,
  })
}
export function useDeleteBudget() {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (id: string) => unwrap(api.finance.budgets[':id'].$delete({ param: { id } })), onSuccess: refresh })
}

type SponsorCreate = Parameters<typeof api.finance.sponsors.$post>[0]['json']
export function useSaveSponsor() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { id?: string; json: SponsorCreate }) =>
      v.id ? unwrap(api.finance.sponsors[':id'].$patch({ param: { id: v.id }, json: v.json })) : unwrap(api.finance.sponsors.$post({ json: v.json })),
    onSuccess: refresh,
  })
}
export function useArchiveSponsor() {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (id: string) => unwrap(api.finance.sponsors[':id'].$delete({ param: { id } })), onSuccess: refresh })
}

type SponsorshipCreate = Parameters<(typeof api.finance.sponsors)[':id']['sponsorships']['$post']>[0]['json']
export function useSaveSponsorship() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { sponsorId: string; id?: string; json: SponsorshipCreate }) =>
      v.id
        ? unwrap(api.finance.sponsorships[':id'].$patch({ param: { id: v.id }, json: v.json }))
        : unwrap(api.finance.sponsors[':id'].sponsorships.$post({ param: { id: v.sponsorId }, json: v.json })),
    onSuccess: refresh,
  })
}
export function useDeleteSponsorship() {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (id: string) => unwrap(api.finance.sponsorships[':id'].$delete({ param: { id } })), onSuccess: refresh })
}
export function useRecordPayment() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { id: string; bookedOn: string; amountCents?: number }) =>
      unwrap(api.finance.sponsorships[':id'].payments.$post({ param: { id: v.id }, json: { bookedOn: v.bookedOn, ...(v.amountCents ? { amountCents: v.amountCents } : {}) } })),
    onSuccess: refresh,
  })
}

export const ledgerCsv = (season: string) => `/api/finance/ledger/export.csv?season=${season}`
